import axios from 'axios'
import React, { useCallback, Dispatch, useRef, useMemo } from 'react'
import { apiVersion } from '../../../const/api'
import { UiState } from '../../../types'
import {
  validateEmail,
  validateName,
  validatePhone,
} from '../../../utils/Validate'

export const HANDY_TRIAL_ACCEPT_URI = `/handy/${apiVersion}/trial/accept`
// iOSがアラート文言をフックしているので、文言は変えない
const ALERT_MESSAGE = 'サーバーに接続できません。もう一度お試ししてください。'
const THANKS_PAGE_URL = 'https://pages.airregi.jp/oes-trial-agreement-done.html'

// TODO: UT実装
const submitMarketo = (
  formRef: React.RefObject<HTMLFormElement>,
  buttonRef: React.RefObject<HTMLButtonElement>
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  buttonRef.current = formRef.current?.querySelector('button[type="submit"]')
  if (buttonRef.current instanceof HTMLButtonElement) {
    buttonRef.current.click()
  }
}

export const useHandleClick = (
  trialMode: TrialMode,
  isConsulRequestChecked: boolean,
  setIsLoading: Dispatch<React.SetStateAction<boolean>>,
  marketoFormRef: React.RefObject<HTMLFormElement>,
  state: UiState,
  setState: Dispatch<React.SetStateAction<UiState>>
) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const validate = useValidate(state, setState)

  return useCallback(async () => {
    if (isConsulRequestChecked && !validate()) {
      return
    }
    const X_APP_KEY = 'xapp'
    const searchParam = new URLSearchParams(location.search)
    // TODO: 1.62系がいなくなったら '' に変更
    const xapp =
      searchParam.get(X_APP_KEY)?.replace('%20', ' ') || 'AirOES 1.62.0'
    const reqBody: TrialAcceptRequestForm = {
      needsContact: isConsulRequestChecked,
      trialMode: trialMode,
    }
    setIsLoading(true)
    axios({
      method: 'post',
      url: HANDY_TRIAL_ACCEPT_URI,
      baseURL: process.env.VITE_HANDY_SERVER_ENDPOINT,
      withCredentials: true,
      headers: { 'X-APP': xapp },
      data: reqBody,
    })
      .then(res => {
        setIsLoading(false)
        if (res.status !== 200) {
          console.error('サーバーからステータスコード200以外が返却されました')
          console.info(res)
          alert(ALERT_MESSAGE)
          return
        }
        if (isConsulRequestChecked) {
          submitMarketo(marketoFormRef, submitButtonRef)
        } else {
          window.location.href = THANKS_PAGE_URL
        }
      })
      .catch(err => {
        setIsLoading(false)
        console.error(err)
        alert(ALERT_MESSAGE)
      })
  }, [
    isConsulRequestChecked,
    validate,
    trialMode,
    setIsLoading,
    marketoFormRef,
  ])
}

export const useIsDisabled = (
  isConsulRequestChecked: boolean,
  state: UiState
) => {
  return useMemo(() => {
    if (!isConsulRequestChecked) {
      return false
    }
    return (
      state.storeName.errors.length > 0 ||
      state.firstName.errors.length > 0 ||
      state.lastName.errors.length > 0 ||
      state.email.errors.length > 0 ||
      state.phone.errors.length > 0
    )
  }, [isConsulRequestChecked, state])
}

const useValidate = (
  state: UiState,
  setState: Dispatch<React.SetStateAction<UiState>>
) => {
  return useCallback(() => {
    const storeNameErr = validateName(state.storeName.value)
    const lastNameErr = validateName(state.lastName.value)
    const firstNameErr = validateName(state.firstName.value)
    const emailErr = validateEmail(state.email.value, true)
    const phoneErr = validatePhone(state.phone.value)
    setState({
      ...state,
      storeName: {
        value: state.storeName.value,
        errors: storeNameErr,
      },
      lastName: {
        value: state.lastName.value,
        errors: lastNameErr,
      },
      firstName: {
        value: state.firstName.value,
        errors: firstNameErr,
      },
      email: {
        value: state.email.value,
        shouldCheckFormat: true,
        errors: emailErr,
      },
      phone: {
        value: state.phone.value,
        errors: phoneErr,
      },
    })
    return (
      storeNameErr.length === 0 &&
      lastNameErr.length === 0 &&
      firstNameErr.length === 0 &&
      emailErr.length === 0 &&
      phoneErr.length === 0
    )
  }, [setState, state])
}

/**
 * お試し種別。
 *
 * 詳しくはサーバーのTrialModeを参照。`NON_TRIAL`はお試し同意webviewからは送ることがないので定義していない。
 */
export type TrialMode = 'TRIAL_HANDY_KM' | 'TRIALDEMO_HANDY'

/**
 * お試し同意APIのリクエストフォーム。
 *
 * 詳細はswaggerを参照
 * https://dev-swagger-site-oes.s3.ap-northeast-1.amazonaws.com/main/dist/index.html#/お試し/acceptUsingPOST
 */
export type TrialAcceptRequestForm = {
  needsContact: boolean
  trialMode: TrialMode
}
